// @flow

import * as React from "react";
import { password, ahpraNumber } from "@constants/Validators";
import { Row, Col } from "@components/organisms/Grid";
import { ReactForm, ReactFormField } from "@adrenalin/react-form";
import GoogleAddressInput from "@components/atoms/GoogleAddressInput";

type PropsType = {
    action: ?string,
    hiddenInputs: ?any,
};

const ContactForm = (props: PropsType): React.Node => {
    const streetNumberRef = React.useRef();
    const streetNameRef = React.useRef();
    const cityRef = React.useRef();
    const stateRef = React.useRef();
    const postcodeRef = React.useRef();
    const countryRef = React.useRef();
    const recaptchaContainer = React.useRef();

    const [fullAddressEditing, setfullAddressEditing] = React.useState(false);

    // const [formErrors, setFormErrors] = React.useState(
    //     ([]: Array<{ error: string, label: string }>)
    // );

    const handleGoogleAddressInputChange = event => {
        const addressComponents = event.target.data;
        console.log(event);
        if (addressComponents && addressComponents.address_components) {
            // map the google address object data to the address sub fields
            if (streetNumberRef.current) {
                streetNumberRef.current.value = addressComponents
                    .address_components[0]
                    ? addressComponents.address_components[0].short_name
                    : "";
            }
            if (streetNameRef.current) {
                streetNameRef.current.value = addressComponents
                    .address_components[1]
                    ? addressComponents.address_components[1].long_name
                    : "";
            }
            if (cityRef.current) {
                cityRef.current.value = addressComponents.address_components[2]
                    ? addressComponents.address_components[2].long_name
                    : "";
            }
            if (stateRef.current) {
                stateRef.current.value = addressComponents.address_components[4]
                    ? addressComponents.address_components[4].short_name
                    : "";
            }
            if (postcodeRef.current) {
                postcodeRef.current.value = addressComponents
                    .address_components[6]
                    ? addressComponents.address_components[6].long_name
                    : "";
            }
            if (countryRef.current) {
                countryRef.current.value = addressComponents
                    .address_components[5]
                    ? addressComponents.address_components[5].long_name
                    : "";
            }
        }
    };

    const handleCantFindAddressClick = () => {
        // toggle the address area
        setfullAddressEditing(!fullAddressEditing);
    };

    // Run on onSuccess & onFail in order to validate ReactForm and manually validated fields
    // Will prevent default if an error is found in either sets of fields
    const attemptFormSubmit = (
        inputs: any,
        states: any,
        event: ?SyntheticEvent<HTMLFormElement>
    ) => {
        let errors = states.reduce((prev: Array<any>, input: any): any => {
            if (input.errors && input.errors[0]) {
                return [
                    ...prev,
                    {
                        label: input.label,
                        error: input.errors[0],
                    },
                ];
            }
            return prev;
        }, []);

        // Recaptcha
        if (recaptchaContainer.current) {
            const input = recaptchaContainer.current.querySelector(
                "input[name='captcha_response']"
            );
            if (!input || input.value === "") {
                recaptchaContainer.current.classList.add("is-invalid");
                errors = [
                    ...errors,
                    {
                        label: "reCAPTCHA:",
                        error: "You must fill out the reCAPTCHA",
                    },
                ];
            } else {
                recaptchaContainer.current.classList.remove("is-invalid");
            }
        }

        // Set form errors in state
        // setFormErrors(errors);

        if (errors.length > 0) {
            // Invalid
            if (event) {
                event.preventDefault();
            }
            // if (topOfFormRef.current) {
            //     scrollToEl(topOfFormRef.current);
            // }
            console.log(errors);
        } else {
            // Valid - do nothing to let the form submit as normal
        }
    };

    return (
        <ReactForm
            validators={[password, ahpraNumber]}
            pageSubmit
            method="POST"
            action={props.action || ""}
            onSuccess={attemptFormSubmit}
            onError={attemptFormSubmit}
        >
            <Row>
                <Col xs={12} sm={6}>
                    <ReactFormField
                        type="select"
                        name="i_am_a"
                        required={true}
                        label="I am a"
                    >
                        <option value="">- Select -</option>
                        <option value="consumer">Consumer</option>
                        <option value="health-professional">
                            Health Professional
                        </option>
                    </ReactFormField>
                </Col>

                <Col xs={12} sm={6}>
                    <ReactFormField
                        type="select"
                        name="title"
                        required={true}
                        label="Title"
                    >
                        <option value="">- Select -</option>
                        <option value="prof">Prof</option>
                        <option value="dr">Dr</option>
                        <option value="mr">Mr</option>
                        <option value="mrs">Mrs</option>
                        <option value="ms">Ms</option>
                    </ReactFormField>
                </Col>
            </Row>
            <Row>
                <Col xs={12} sm={6}>
                    <ReactFormField
                        type="text"
                        name="first_name"
                        required={true}
                        errorMessages={{
                            required: "Please enter a first name.",
                        }}
                        label="First name"
                        placeholder="Janet"
                        maxLength="255"
                    />
                </Col>

                <Col xs={12} sm={6}>
                    <ReactFormField
                        type="text"
                        name="last_name"
                        required={true}
                        errorMessages={{
                            required: "Please enter a last name.",
                        }}
                        label="Last name"
                        placeholder="Smith"
                        maxLength="255"
                    />
                </Col>
            </Row>

            <div className="form-field-wrapper address">
                <ReactFormField
                    fieldComponent={GoogleAddressInput}
                    type="text"
                    name="address"
                    required={!fullAddressEditing}
                    errorMessages={{
                        required: "Please enter and select an address.",
                    }}
                    label="Address"
                    placeholder={`${
                        !fullAddressEditing
                            ? "Sydney CBD, NSW 2000, Australia"
                            : ""
                    }`}
                    onChange={handleGoogleAddressInputChange}
                    autoComplete="off"
                    disabled={fullAddressEditing}
                />

                <button
                    onClick={handleCantFindAddressClick}
                    type="button"
                    className="text-button text-button__address"
                >
                    Can't find your address?
                </button>
            </div>

            <div className={`hidden ${fullAddressEditing ? "is-active" : ""}`}>
                <Row>
                    <Col xs={12} sm={6}>
                        <ReactFormField
                            ref={streetNumberRef}
                            type="text"
                            name="street_number"
                            label="Street Number"
                            placeholder="123"
                        />
                    </Col>

                    <Col xs={12} sm={6}>
                        <ReactFormField
                            ref={streetNameRef}
                            type="text"
                            name="street_name"
                            label="Street Name"
                            placeholder="Fake St"
                        />
                    </Col>
                </Row>

                <ReactFormField
                    ref={cityRef}
                    type="text"
                    name="city"
                    label="City"
                    placeholder="Sydney"
                />

                <Row>
                    <Col xs={12} sm={6}>
                        <ReactFormField
                            ref={stateRef}
                            type="text"
                            name="state"
                            label="State"
                            placeholder="New South Whales"
                        />
                    </Col>

                    <Col xs={12} sm={6}>
                        <ReactFormField
                            ref={postcodeRef}
                            type="text"
                            name="post_code"
                            label="Post Code"
                            placeholder="2050"
                        />
                    </Col>
                </Row>

                <ReactFormField
                    ref={countryRef}
                    type="text"
                    name="country"
                    label="Country"
                    placeholder="Australia"
                />
            </div>

            <ReactFormField
                type="number"
                name="phone_number"
                required={true}
                errorMessages={{
                    required: "Please enter a phone number.",
                }}
                label="Phone Number"
                placeholder="Enter Phone Number here"
            />
            <ReactFormField
                type="email"
                name="mail"
                required={true}
                validators={["email"]}
                errorMessages={{
                    required: "Please enter an email.",
                }}
                label="Email"
                placeholder="Enter Email here"
            />
            <ReactFormField
                type="text"
                name="subject"
                required={true}
                errorMessages={{
                    required: "Please enter a subject.",
                }}
                label="Subject"
                placeholder="Enter Subject here"
                maxLength="255"
            />
            <ReactFormField
                type="textarea"
                name="message"
                required={true}
                errorMessages={{
                    required: "Please enter a message.",
                }}
                label="Message"
                placeholder="Enter your message here"
            />
            {props.captcha ? (
                <div className="recaptcha-container" ref={recaptchaContainer}>
                    {props.captcha}
                </div>
            ) : null}
            <div className="center-xs">
                <button type="submit" className="button">
                    Send email
                </button>
            </div>
            {props.hiddenInputs || null}
        </ReactForm>
    );
};

export default ContactForm;
